import { useState } from "react";

import { Icon } from "../icons/Icon";

interface TextInputPropsBase {
    id?: string;
    name?: string;
    type?: "text" | "email" | "password" | "number" | "tel";
    rows?: number;
    autoComplete?: "email" | "password" | "tel";
    required?: boolean;
    value: string;
    onChange: (value: string) => void;
    onBlur?: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    isValid?: boolean;
    className?: string;
}

interface SingleRowTextInputProps extends TextInputPropsBase {
    type?: "text" | "email" | "password" | "number" | "tel";
    rows?: never;
    additionalProps?: Partial<React.InputHTMLAttributes<HTMLInputElement>>;
}

interface MultiRowsTextInputProps extends TextInputPropsBase {
    type?: "text";
    rows: number;
    additionalProps?: Partial<
        React.TextareaHTMLAttributes<HTMLTextAreaElement>
    >;
}

type TextInputProps = SingleRowTextInputProps | MultiRowsTextInputProps;

export const isValidPhoneNumber = (value: string) => {
    return /^0{1}[0-9]{9}$/.test(value);
};

export const TextInput = ({
    id,
    name,
    type = "text",
    rows,
    autoComplete,
    required,
    value,
    onChange,
    onBlur,
    placeholder,
    disabled,
    isValid = true,
    className,
    additionalProps,
}: TextInputProps) => {
    const [isDirty, setIsDirty] = useState(false);

    const handleChange = (value: string) => {
        if (!isDirty) {
            setIsDirty(true);
        }
        onChange(value);
    };

    const isInvalid =
        (isDirty && required && !value) ||
        (type === "tel" && !!value && !isValidPhoneNumber(value)) ||
        isValid === false;

    const props = {
        ...additionalProps,
        id,
        name,
        type,
        autoComplete,
        required,
        placeholder,
        value: value || "",
        disabled,
        className: `${value ? "filled" : ""} ${disabled ? "readonly" : ""} ${
            isInvalid ? "invalid" : "valid"
        } ${className}`,
    };

    return (
        <>
            <div className="relative w-full">
                {rows ? (
                    <textarea
                        {...(props as Partial<
                            React.TextareaHTMLAttributes<HTMLTextAreaElement>
                        >)}
                        {...(additionalProps as Partial<
                            React.TextareaHTMLAttributes<HTMLTextAreaElement>
                        >)}
                        rows={rows}
                        onChange={(e) => handleChange(e.target.value)}
                        onBlur={(e) => onBlur?.(e.target.value)}
                    />
                ) : (
                    <input
                        {...(props as Partial<
                            React.InputHTMLAttributes<HTMLInputElement>
                        >)}
                        {...(additionalProps as Partial<
                            React.InputHTMLAttributes<HTMLInputElement>
                        >)}
                        onChange={(e) => handleChange(e.target.value)}
                        onBlur={(e) => onBlur?.(e.target.value)}
                    />
                )}
                {value && (
                    <div
                        onClick={() => {
                            handleChange("");
                            onBlur?.("");
                        }}
                        className="input-clear-icon"
                    >
                        <Icon name="x" />
                    </div>
                )}
            </div>
        </>
    );
};
